import type { ConfiguredImageProviders } from '@nuxt/image';
import type { ImgHTMLAttributes } from 'vue';
import { breakpointsTailwind } from '@vueuse/core';
import type { BaseImageProperties } from './nuxt-image-internals';

export interface PictureProperties<
  Provider extends keyof ConfiguredImageProviders,
> extends BaseImageProperties<Provider> {
  /** Declared instead of travelling through the fallthrough attributes, so it cannot be forgotten. */
  alt: string;
  /** Required here, unlike NuxtPicture: the picture, the wide one when a narrow one exists. */
  src: string;
  /** WHICH FILE: replaces `src` below `breakpoint`. Without it, this behaves like NuxtPicture. */
  narrowSrc?: string;
  /** Viewport width, in pixels, up to which `narrowSrc` replaces `src`. Defaults to Tailwind's `lg`. */
  breakpoint?: number;
  legacyFormat?: string;
  imgAttrs?: ImgHTMLAttributes;
  /**
   * A data URI of a tiny thumbnail of `src`, shown blurred under the image until it has loaded: it comes with the
   * HTML. Defaults to the one the build derived for a picture of `public/images`, so a first-render image needs
   * nothing here; a picture served by the BFF hands its own. Read once, when the picture mounts.
   */
  placeholder?: string;
  /**
   * The same for `narrowSrc`, behind the same media as the picture: a thumbnail of another picture than the one
   * shown would be a lie. Defaults like `placeholder`; without one, no range shows a thumbnail.
   */
  narrowPlaceholder?: string;
  /** No thumbnail at all: a picture mounted over another one has a better background already. */
  noPlaceholder?: boolean;
}

export interface PictureVariant<Provider extends keyof ConfiguredImageProviders> {
  src: string;
  /** What the box measures over this variant's whole range — one value, never a list. */
  size?: string;
  /** Seeds the srcset with the candidate widths this variant's own range can ask for. */
  ladder?: BaseImageProperties<Provider>['sizes'];
  media?: string;
}

/** Tailwind's `lg`, where a narrow and a wide picture switch when no breakpoint is given. */
const WIDE_BREAKPOINT = 1024;

/** The media from which `src` takes over from `narrowSrc` — the thumbnail switches on the very same one. */
export const fileSwitchMedia = (breakpoint?: number): string => `(min-width: ${breakpoint ?? WIDE_BREAKPOINT}px)`;

// Inherited, never restated: a key in `sizes` and the same key in `class` must be the same number,
// and @nuxt/image happens to ship these five too. This is the same map the layout itself reads
// through useBreakpoints (components/search/form.vue and four others).
const SCREENS: Record<string, number> = breakpointsTailwind;

// Nothing in this codebase switches layout above xl (`2xl:` appears nowhere), so an open-ended tier
// ladders no further: 1280 at 2x is 2560, which covers the widest viewport the layout reacts to.
const LADDER_TOP = breakpointsTailwind.xl;

interface SizeTier {
  /** Viewport width from which this size applies — 0 for the leading bare value. */
  from: number;
  size: string;
}

// `sizes="100vw md:50vw xl:336px"` — the bare value is the base, each Tailwind key is a min-width
// that holds until the next. Unlike @nuxt/image's own grammar, nothing is shifted and nothing is
// max-width, so a tier boundary and the Tailwind class that causes it are the same number.
const parseTiers = (sizes: string | undefined): SizeTier[] =>
  (sizes?.split(/[\s,]+/).filter(Boolean) ?? [])
    .map((entry) => {
      const [key, value] = entry.includes(':') ? entry.split(':') : [undefined, entry];
      return { from: key === undefined ? 0 : SCREENS[key], size: value };
    })
    .filter((tier): tier is SizeTier => tier.from !== undefined && tier.size.length > 0)
    .toSorted((first, second) => first.from - second.from);

// One candidate per screen the tier spans, and only those: a wider one is a file nothing displays,
// a narrower one is a blurry box. Every entry repeats the tier's single size, so the media
// @nuxt/image derives from these keys can never change what the browser reads.
const ladderFor = (tier: SizeTier, until: number): Record<string, string> => {
  const spanned = Object.keys(SCREENS).filter((key) => {
    const width = SCREENS[key] ?? 0;
    return width >= tier.from && width <= until;
  });
  return Object.fromEntries(spanned.map((key) => [key, tier.size]));
};

// Widest first — the browser takes the first <source> that matches, and the last entry carries no
// media, so it has to be the default. The two switches are independent (which file, how wide) and
// share one ordered list of min-width boundaries: a boundary takes the file and the size that apply
// there, which is what lets a crop change at 450 and a box stop growing at xl in the same picture.
export const pictureVariants = <Provider extends keyof ConfiguredImageProviders>(
  properties: PictureProperties<Provider>,
): PictureVariant<Provider>[] => {
  const sourceBreakpoint = properties.breakpoint ?? WIDE_BREAKPOINT;
  const tiers = parseTiers(typeof properties.sizes === 'string' ? properties.sizes : undefined);
  const fileSwitch = properties.narrowSrc ? [sourceBreakpoint] : [];
  const boundaries = [...new Set([0, ...tiers.map((tier) => tier.from), ...fileSwitch])];
  return boundaries
    .toSorted((first, second) => second - first)
    .map((boundary) => {
      const index = tiers.findLastIndex((candidate) => candidate.from <= boundary);
      const tier = tiers[index];
      // A tier above LADDER_TOP (a `2xl:` one) would otherwise span no screen at all.
      const until = tiers[index + 1]?.from ?? Math.max(LADDER_TOP, tier?.from ?? 0);
      const narrow = properties.narrowSrc !== undefined && boundary < sourceBreakpoint;
      return {
        src: narrow && properties.narrowSrc ? properties.narrowSrc : properties.src,
        size: tier?.size,
        // No tier at all: nothing was declared, so this behaves like NuxtPicture and lets
        // @nuxt/image fall back to the whole screens ladder.
        ladder: tier ? ladderFor(tier, until) : properties.sizes,
        media: boundary > 0 ? `(min-width: ${boundary}px)` : undefined,
      };
    });
};
